import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * AlertDialog — the confirmation before a destructive or consequential act
 * (ADR-0088 D2): Admin's deletes, deactivations and discards, and Student's
 * "all coaches are busy".
 *
 * Built on Base UI (ADR-0088 D3), replacing Admin's Radix alert dialog. It is
 * modal and, unlike a Dialog, is not dismissed by a click on the scrim: a
 * stray click outside must not read as an answer. Escape still cancels.
 *
 * Two Radix behaviors the wrapper keeps, so call sites written against Radix
 * are unchanged, each pinned by a test:
 *
 * - **Cancel takes focus on open**, wherever it sits in the DOM. Base UI's
 *   default is the first tabbable element, which in several confirms is the
 *   destructive action or a reason field. The safe answer must be the one
 *   Enter gives without reading.
 * - **`event.preventDefault()` in an action's or cancel's `onClick` keeps the
 *   dialog open**, so a confirm can stay up while its request runs. Base UI's
 *   own way is `preventBaseUIHandler()`, which the wrapper calls.
 *
 * The size carries the shape, as for a Dialog: `default` is Admin's dense
 * confirm, `lg` Student's dialog card, whose title and text take the page's
 * type. The action and cancel are the shared Button, `danger` and
 * `secondary` unless a call site names another variant.
 */
import { RefreshBlocker } from './app-update-hooks.js';
import { AlertDialog as AlertDialogPrimitive } from '@base-ui/react/alert-dialog';
import { createContext, forwardRef, useContext, useRef, } from 'react';
import { asChildProps } from './as-child.js';
import { Button } from './button.js';
import { cn } from './cn.js';
import { modalMotion, modalPosition, modalSurface, overlayBackdrop } from './overlay-shape.js';
export const AlertDialog = AlertDialogPrimitive.Root;
export const AlertDialogTrigger = forwardRef(({ asChild, children, ...props }, ref) => (_jsx(AlertDialogPrimitive.Trigger, { ref: ref, "data-slot": "alert-dialog-trigger", ...props, ...asChildProps({ asChild, children }) })));
AlertDialogTrigger.displayName = 'AlertDialogTrigger';
/** Cancel registers itself here so the popup can focus it on open. */
const CancelFocusContext = createContext(null);
const AlertDialogSizeContext = createContext('default');
export const AlertDialogContent = forwardRef(({ className, initialFocus, size = 'default', children, ...props }, ref) => {
    const cancelRef = useRef(null);
    return (_jsxs(AlertDialogPrimitive.Portal, { children: [_jsx(AlertDialogPrimitive.Backdrop, { "data-slot": "alert-dialog-overlay", className: overlayBackdrop }), _jsx(AlertDialogPrimitive.Popup, { ref: ref, "data-slot": "alert-dialog-content", "data-size": size, 
                // An empty ref falls back to Base UI's default (the first tabbable
                // element), so a confirm without a Cancel still moves focus inside.
                initialFocus: initialFocus ?? cancelRef, className: cn(modalPosition, modalMotion, modalSurface[size], size === 'default' && 'max-w-lg p-6', className), ...props, children: _jsxs(AlertDialogSizeContext.Provider, { value: size, children: [_jsx(RefreshBlocker, {}), _jsx(CancelFocusContext.Provider, { value: cancelRef, children: children })] }) })] }));
});
AlertDialogContent.displayName = 'AlertDialogContent';
export function AlertDialogHeader({ children }) {
    return _jsx("div", { className: "space-y-2", children: children });
}
export const AlertDialogTitle = forwardRef(({ className, ...props }, ref) => {
    const size = useContext(AlertDialogSizeContext);
    return (_jsx(AlertDialogPrimitive.Title, { ref: ref, className: cn(size === 'default' && 'text-section-title font-medium text-text-primary', className), ...props }));
});
AlertDialogTitle.displayName = 'AlertDialogTitle';
export const AlertDialogDescription = forwardRef(({ className, ...props }, ref) => {
    const size = useContext(AlertDialogSizeContext);
    return (_jsx(AlertDialogPrimitive.Description, { ref: ref, className: cn(size === 'default' && 'text-body text-text-secondary', className), ...props }));
});
AlertDialogDescription.displayName = 'AlertDialogDescription';
/**
 * Cancel first in the DOM, so it is the first thing Tab reaches, and last on
 * screen (`flex-row-reverse`), where a confirm button is expected.
 */
export function AlertDialogFooter({ children }) {
    return _jsx("div", { className: "mt-6 flex flex-row-reverse gap-3", children: children });
}
/**
 * Radix's semantics for a closing button's click: the call site's handler runs
 * first, and `event.preventDefault()` in it keeps the dialog open.
 */
function keepOpenWhenPrevented(onClick) {
    return (event) => {
        onClick?.(event);
        if (event.defaultPrevented)
            event.preventBaseUIHandler();
    };
}
/** The act being confirmed. `danger` unless the act is not destructive. */
export const AlertDialogAction = forwardRef(({ className, onClick, variant = 'danger', size, block, ...props }, ref) => (_jsx(AlertDialogPrimitive.Close, { ref: ref, render: _jsx(Button, { variant: variant, size: size, block: block, className: className }), onClick: keepOpenWhenPrevented(onClick), ...props })));
AlertDialogAction.displayName = 'AlertDialogAction';
/** The safe answer, focused when the dialog opens. */
export const AlertDialogCancel = forwardRef(({ className, onClick, variant = 'secondary', size, block, ...props }, forwardedRef) => {
    const cancelRef = useContext(CancelFocusContext);
    return (_jsx(AlertDialogPrimitive.Close, { ref: (node) => {
            if (cancelRef)
                cancelRef.current = node;
            if (typeof forwardedRef === 'function')
                forwardedRef(node);
            else if (forwardedRef)
                forwardedRef.current = node;
        }, render: _jsx(Button, { variant: variant, size: size, block: block, className: className }), onClick: keepOpenWhenPrevented(onClick), ...props }));
});
AlertDialogCancel.displayName = 'AlertDialogCancel';
//# sourceMappingURL=alert-dialog.js.map